import React from 'react';

/**
 * Barre KPI pour afficher un résumé des documents
 */
const DocumentKPIBar = ({ documents }) => {
  // Calculer les KPIs
  const calculateKPIs = () => {
    const totalDocuments = documents.length;
    
    // Total TTC
    const totalTTC = documents.reduce((sum, doc) => sum + (doc.total_ttc || 0), 0);
    
    // Montant payé (seulement pour factures et bons de commande)
    const totalPaye = documents
      .filter(doc => doc.type !== 'devis')
      .reduce((sum, doc) => sum + (doc.montant_paye || 0), 0);
    
    // Reste à payer
    const totalReste = documents
      .filter(doc => doc.type !== 'devis')
      .reduce((sum, doc) => sum + (doc.reste_a_payer || 0), 0);
    
    // Pourcentage payé
    const pourcentagePaye = totalTTC > 0 ? ((totalPaye / totalTTC) * 100).toFixed(1) : 0;
    
    // Répartition des statuts
    const statuts = documents.reduce((acc, doc) => {
      acc[doc.statut] = (acc[doc.statut] || 0) + 1;
      return acc;
    }, {});
    
    // Statuts de paiement
    const paymentStatuses = {
      paye: 0,
      partiel: 0,
      nonPaye: 0
    };
    
    documents.forEach(doc => {
      if (doc.type !== 'devis') {
        const montantPaye = doc.montant_paye || 0;
        const totalTtc = doc.total_ttc || 0;
        
        if (montantPaye >= totalTtc) {
          paymentStatuses.paye++;
        } else if (montantPaye > 0) {
          paymentStatuses.partiel++;
        } else {
          paymentStatuses.nonPaye++;
        }
      }
    });
    
    // Workflow - Prospects vs Clients (basés sur type_contact du client)
    const offresProspects = documents.filter(d => d.client?.type_contact === 'Client potentiel').length;
    const documentsClients = documents.filter(d => d.client?.type_contact === 'Client actuel').length;
    
    return {
      totalDocuments,
      totalTTC,
      totalPaye,
      totalReste,
      pourcentagePaye,
      statuts,
      paymentStatuses,
      offresProspects,
      documentsClients
    };
  };
  
  const kpis = calculateKPIs();
  
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(0) + 'K';
    }
    return num.toLocaleString();
  };
  
  return (
    <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-700">
          <span className="text-lg font-semibold">📊 Résumé</span>
          <span className="text-sm text-gray-500">({kpis.totalDocuments} documents)</span>
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        {/* Total TTC */}
        <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
          <div className="text-xs text-gray-500 mb-1">Total TTC</div>
          <div className="text-2xl font-bold text-gray-900">
            {formatNumber(kpis.totalTTC)}
          </div>
          <div className="text-xs text-gray-400 mt-1">CFA</div>
        </div>
        
        {/* Montant Payé */}
        <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
          <div className="text-xs text-gray-500 mb-1">Payé</div>
          <div className="text-2xl font-bold text-green-600">
            {formatNumber(kpis.totalPaye)}
          </div>
          <div className="text-xs text-green-500 mt-1">
            {kpis.pourcentagePaye}%
          </div>
        </div>
        
        {/* Reste à Payer */}
        <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
          <div className="text-xs text-gray-500 mb-1">Reste</div>
          <div className="text-2xl font-bold text-red-600">
            {formatNumber(kpis.totalReste)}
          </div>
          <div className="text-xs text-red-500 mt-1">
            {kpis.totalReste > 0 ? 'À recouvrer' : 'Aucun'}
          </div>
        </div>
        
        {/* Statuts de Paiement */}
        <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
          <div className="text-xs text-gray-500 mb-1">Paiements</div>
          <div className="flex items-center gap-2 mt-2">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-xs font-medium">{kpis.paymentStatuses.paye}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span className="text-xs font-medium">{kpis.paymentStatuses.partiel}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-xs font-medium">{kpis.paymentStatuses.nonPaye}</span>
            </div>
          </div>
          <div className="text-xs text-gray-400 mt-1">
            Payé / Partiel / Non payé
          </div>
        </div>
      </div>
      
      {/* Barre de statuts */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <span className="text-xs text-gray-500">Statuts:</span>
        {Object.entries(kpis.statuts).map(([statut, count]) => (
          <div key={statut} className="flex items-center gap-1">
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              statut === 'brouillon' ? 'bg-gray-100 text-gray-700' :
              statut === 'envoyé' ? 'bg-blue-100 text-blue-700' :
              statut === 'acceptée' ? 'bg-green-100 text-green-700' :
              statut === 'refusée' ? 'bg-red-100 text-red-700' :
              statut === 'expiré' ? 'bg-orange-100 text-orange-700' :
              statut === 'annulé' ? 'bg-purple-100 text-purple-700' :
              'bg-emerald-100 text-emerald-700'
            }`}>
              {statut} ({count})
            </span>
          </div>
        ))}
      </div>
      
      {/* Barre Workflow - Prospects vs Clients */}
      {(kpis.offresProspects > 0 || kpis.documentsClients > 0) && (
        <div className="mt-3 flex items-center gap-3 flex-wrap border-t border-gray-200 pt-3">
          <span className="text-xs text-gray-500 font-medium">Workflow:</span>
          {kpis.offresProspects > 0 && (
            <div className="flex items-center gap-1">
              <span className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-700">
                🎯 Prospects ({kpis.offresProspects})
              </span>
            </div>
          )}
          {kpis.documentsClients > 0 && (
            <div className="flex items-center gap-1">
              <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                ✅ Clients actuels ({kpis.documentsClients})
              </span>
            </div>
          )}
          <span className="text-xs text-gray-400">
            {kpis.offresProspects > 0 
              ? `${kpis.offresProspects} document${kpis.offresProspects > 1 ? 's' : ''} pour prospects`
              : 'Tous les documents pour clients actuels'
            }
          </span>
        </div>
      )}
    </div>
  );
};

export default DocumentKPIBar;
